import React from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import { buildInlineDiffSegments } from "components/organisms/manuscript/editor/ai/edit/diff";

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.48);
`;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    width: min(1080px, calc(100vw - 32px));
    height: min(760px, calc(100vh - 32px));
    border: 1px solid #d4dee8;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 20px 36px rgba(15, 23, 42, 0.24);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
`;

const HeaderTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const Title = styled.div`
    font-size: 16px;
    font-weight: 800;
    color: #17314a;
`;

const Subtitle = styled.div`
    font-size: 12px;
    color: #64748b;
`;

const CloseButton = styled.button`
    height: 32px;
    padding: 0 12px;
    border: 1px solid #d6e0ea;
    border-radius: 8px;
    background: #ffffff;
    color: #32485e;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const Body = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: 320px minmax(0, 1fr);
    min-height: 0;
    overflow: hidden;
`;

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 16px;
    border-right: 1px solid #e2e8f0;
    background: #f8fafc;
`;

const Main = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    padding: 16px 18px;
    overflow: hidden;
`;

const SectionTitle = styled.div`
    font-size: 12px;
    font-weight: 800;
    color: #203249;
`;

const ErrorList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    overflow: auto;
`;

const ErrorItem = styled.div`
    padding: 10px;
    border: 1px solid #f1c7c7;
    border-radius: 8px;
    background: #fff8f8;
    color: #203249;
`;

const ErrorMeta = styled.div`
    margin-bottom: 4px;
    color: #b4232b;
    font-size: 12px;
    font-weight: 800;
`;

const ErrorMessage = styled.div`
    font-size: 12px;
    line-height: 1.5;
    word-break: break-word;
`;

const Summary = styled.div`
    min-height: 38px;
    padding: 10px 12px;
    border: 1px solid #dbe5ef;
    border-radius: 8px;
    background: #f8fafc;
    color: #203249;
    font-size: 13px;
    line-height: 1.5;
`;

const DiffBox = styled.div`
    flex: 1;
    min-height: 0;
    padding: 12px;
    border: 1px solid #d6e0eb;
    border-radius: 8px;
    background: #ffffff;
    color: #203249;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: auto;
`;

const Placeholder = styled.span`
    color: #8ba0b5;
    font-family: inherit;
`;

const DiffSegment = styled.span`
    ${(props) =>
        props.$type === "add"
            ? "background: #dff7e8; color: #17613c;"
            : props.$type === "remove"
              ? "background: #ffe2e2; color: #9f1d1d; text-decoration: line-through;"
              : ""}
`;

const DiffBlock = styled.div`
    margin-bottom: 14px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DiffBlockTitle = styled.div`
    margin-bottom: 6px;
    color: #64748b;
    font-family: inherit;
    font-size: 11px;
    font-weight: 800;
`;

const Message = styled.div`
    color: ${(props) => (props.$error ? "#b4232b" : "#52677d")};
    font-size: 12px;
    line-height: 1.5;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid #e2e8f0;
    background: #ffffff;
`;

const ActionButton = styled.button`
    height: 34px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$primary ? "#0f8f6f" : "#d6e0ea")};
    border-radius: 8px;
    background: ${(props) => (props.$primary ? "#0f8f6f" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#32485e")};
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#10765d" : "#eef3f7")};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
`;

function lineRangeLabel(replacement) {
    const startLine = Number(replacement?.start_line);
    const endLine = Number(replacement?.end_line || startLine);
    if (!Number.isFinite(startLine)) {
        return "수정 범위";
    }
    if (startLine === endLine) {
        return `L${startLine}`;
    }
    return `L${startLine}-L${endLine}`;
}

function buildReplacementDiffBlocks(currentContent, replacements) {
    const lines = String(currentContent || "").split("\n");
    return (Array.isArray(replacements) ? replacements : []).map((replacement, index) => {
        const startLine = Number(replacement?.start_line);
        const endLine = Number(replacement?.end_line || startLine);
        const oldText =
            Number.isFinite(startLine) && Number.isFinite(endLine) && startLine >= 1 && endLine >= startLine
                ? lines.slice(startLine - 1, endLine).join("\n")
                : "";
        const newText = String(replacement?.replacement ?? "");
        return {
            key: `${startLine || "line"}-${endLine || "line"}-${index}`,
            label: lineRangeLabel(replacement),
            segments: buildInlineDiffSegments(oldText, newText),
        };
    });
}

function ManuscriptLatexErrorFixModal({
    open,
    session,
    summary,
    replacements,
    isRunning,
    hasResult,
    errorMessage,
    statusMessage,
    onRequest,
    onStop,
    onApply,
    onClose,
}) {
    if (!open || !session) {
        return null;
    }

    const diagnostics = Array.isArray(session.diagnostics) ? session.diagnostics : [];
    const diffBlocks = hasResult ? buildReplacementDiffBlocks(session.currentContent || "", replacements) : [];
    const hasDiffContent = diffBlocks.some((block) => block.segments.some((segment) => segment.value));

    return createPortal(
        <Backdrop onClick={onClose}>
            <Shell
                role="dialog"
                aria-label="LaTeX 오류 수정"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <Header>
                    <HeaderTitle>
                        <Title>LaTeX 오류 수정</Title>
                        <Subtitle>{session.nodeName || "현재 파일"}의 문법 오류를 AI로 수정합니다.</Subtitle>
                    </HeaderTitle>
                    <CloseButton type="button" onClick={onClose}>
                        닫기
                    </CloseButton>
                </Header>
                <Body>
                    <Sidebar>
                        <SectionTitle>오류 목록</SectionTitle>
                        <ErrorList>
                            {diagnostics.map((diagnostic, index) => (
                                <ErrorItem key={`${diagnostic.line || "line"}-${index}`}>
                                    <ErrorMeta>
                                        L{diagnostic.line || session.line}
                                        {diagnostic.column ? `, C${diagnostic.column}` : ""}
                                    </ErrorMeta>
                                    <ErrorMessage>{diagnostic.message || "LaTeX 문법 오류"}</ErrorMessage>
                                </ErrorItem>
                            ))}
                        </ErrorList>
                    </Sidebar>
                    <Main>
                        <SectionTitle>수정 제안</SectionTitle>
                        <Summary>
                            {isRunning ? (
                                <>
                                    수정안을 생성하고 있습니다
                                    <AiCursor />
                                </>
                            ) : summary ? (
                                summary
                            ) : (
                                <Placeholder>수정 제안을 시작하면 요약이 표시됩니다.</Placeholder>
                            )}
                        </Summary>
                        <DiffBox>
                            {hasResult && hasDiffContent ? (
                                diffBlocks.map((block) => (
                                    <DiffBlock key={block.key}>
                                        <DiffBlockTitle>{block.label}</DiffBlockTitle>
                                        {block.segments.map((segment, index) => (
                                            <DiffSegment key={`${segment.type}-${index}`} $type={segment.type}>
                                                {segment.value}
                                            </DiffSegment>
                                        ))}
                                    </DiffBlock>
                                ))
                            ) : hasResult ? (
                                "변경할 내용이 없습니다."
                            ) : (
                                <Placeholder>수정 diff가 여기에 표시됩니다.</Placeholder>
                            )}
                        </DiffBox>
                        {errorMessage ? <Message $error>{errorMessage}</Message> : null}
                        {statusMessage ? <Message>{statusMessage}</Message> : null}
                    </Main>
                </Body>
                <Footer>
                    <ActionButton type="button" onClick={onStop} disabled={!isRunning}>
                        중지
                    </ActionButton>
                    <ActionButton type="button" onClick={onRequest} disabled={isRunning}>
                        수정 제안
                    </ActionButton>
                    <ActionButton type="button" $primary onClick={onApply} disabled={isRunning || !hasResult}>
                        반영
                    </ActionButton>
                    <ActionButton type="button" onClick={onClose}>
                        취소
                    </ActionButton>
                </Footer>
            </Shell>
        </Backdrop>,
        document.body,
    );
}

export default ManuscriptLatexErrorFixModal;
